import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Edit2, Trash2, Tag } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Card, Modal, ModalHeader, ModalBody, ModalFooter, Input, EmptyState, DeleteConfirmModal } from '../../../components/ui'

interface PostTag {
  id: number; name: string; slug: string; posts_count: number
}

export default function BlogTagsPage() {
  const navigate = useNavigate()
  const [tags, setTags] = useState<PostTag[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState({ name: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [deleteId, setDeleteId] = useState<number | null>(null)

  useEffect(() => { fetchTags() }, [])

  async function fetchTags() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/posts/tags'))
      setTags(res.data.data)
    } catch { /* */ } finally { setLoading(false) }
  }

  function openCreate() {
    setEditingId(null)
    setForm({ name: '' })
    setErrors({})
    setModalOpen(true)
  }

  function openEdit(tag: PostTag) {
    setEditingId(tag.id)
    setForm({ name: tag.name })
    setErrors({})
    setModalOpen(true)
  }

  async function handleSave() {
    if (saving) return
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Nome é obrigatório'
    setErrors(e)
    if (Object.keys(e).length > 0) return

    setSaving(true)
    try {
      if (editingId) {
        await axios.put(buildApiUrl(`/v1/posts/tags/${editingId}`), form)
        toast('success', 'Tag atualizada')
      } else {
        await axios.post(buildApiUrl('/v1/posts/tags'), form)
        toast('success', 'Tag criada')
      }
      setModalOpen(false)
      fetchTags()
    } catch (err: any) {
      toast('error', err.response?.data?.error || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!deleteId) return
    await axios.delete(buildApiUrl(`/v1/posts/tags/${deleteId}`))
    toast('success', 'Tag excluída')
    setDeleteId(null)
    fetchTags()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/blog/posts')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
            <p className="text-gray-500 mt-1">Etiquetas para organizar o conteúdo</p>
          </div>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={openCreate}>Nova Tag</Button>
      </div>

      {loading ? (
        <Card padding="none">
          <div className="p-5 space-y-3">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}</div>
        </Card>
      ) : tags.length === 0 ? (
        <Card>
          <EmptyState icon={<Tag className="w-7 h-7" />} title="Nenhuma tag" description="Crie tags para categorizar os posts."
            action={<Button size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Criar</Button>} />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Posts</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tags.map(tag => (
                  <tr key={tag.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3.5 text-sm font-medium text-gray-900">{tag.name}</td>
                    <td className="px-4 py-3.5 text-xs text-gray-400 font-mono">/{tag.slug}</td>
                    <td className="px-4 py-3.5 text-center text-sm text-gray-700">{tag.posts_count}</td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(tag)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteId(tag.id)} className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>{editingId ? 'Editar Tag' : 'Nova Tag'}</ModalHeader>
        <ModalBody>
          <Input label="Nome" value={form.name} onChange={e => { setForm({ name: e.target.value }); setErrors({}) }}
            placeholder="Ex: Saúde, Educação" error={errors.name} />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} loading={saving}>{editingId ? 'Salvar' : 'Criar'}</Button>
        </ModalFooter>
      </Modal>

      <DeleteConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir tag" message="Tem certeza que deseja excluir esta tag? Os posts não serão afetados." confirmLabel="Excluir" />
    </div>
  )
}
